/**
 * Frontend validation utilities
 */

// Validation regex patterns
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&:;.,#^~|`\-_=+<>{}[\]"'])[A-Za-z\d@$!%*?&:;.,#^~|`\-_=+<>{}[\]"']{8,}$/;
const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,20}$/;
const PHONE_REGEX = /^\+?[\d\s\-()]{10,15}$/;
const TIME_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validateEmail = (email) => {
  if (!email || email.trim() === '') {
    return 'Email is required';
  }
  
  if (!EMAIL_REGEX.test(email)) {
    return 'Please enter a valid email address';
  }
  
  return null;
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validatePassword = (password) => {
  if (!password || password.trim() === '') {
    return 'Password is required';
  }
  
  if (password.length < 8) {
    return 'Password must be at least 8 characters long';
  }
  
  if (!PASSWORD_REGEX.test(password)) {
    return 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
  }
  
  return null;
};

/**
 * Validate password confirmation
 * @param {string} password - Original password
 * @param {string} confirmPassword - Password confirmation
 * @returns {string|null} - Error message or null if valid
 */
export const validatePasswordConfirmation = (password, confirmPassword) => {
  if (!confirmPassword || confirmPassword.trim() === '') {
    return 'Password confirmation is required';
  }
  
  if (password !== confirmPassword) {
    return 'Passwords do not match';
  }
  
  return null;
};

/**
 * Validate username format
 * @param {string} username - Username to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validateUsername = (username) => {
  if (!username || username.trim() === '') {
    return 'Username is required';
  }
  
  if (!USERNAME_REGEX.test(username)) {
    return 'Username must be 3-20 characters long and contain only letters, numbers, and underscores';
  }
  
  return null;
};

/**
 * Validate required field
 * @param {any} value - Value to validate
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} - Error message or null if valid
 */
export const validateRequired = (value, fieldName) => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return `${fieldName} is required`;
  }
  
  return null;
};

/**
 * Validate string length
 * @param {string} value - String to validate
 * @param {number} min - Minimum length
 * @param {number} max - Maximum length
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} - Error message or null if valid
 */
export const validateStringLength = (value, min, max, fieldName) => {
  if (!value || typeof value !== 'string') {
    return null; // Let required validation handle empty values
  }
  
  if (value.length < min) {
    return `${fieldName} must be at least ${min} characters long`;
  }
  
  if (value.length > max) {
    return `${fieldName} must be no more than ${max} characters long`;
  }
  
  return null;
};

/**
 * Validate number range
 * @param {number} value - Number to validate
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} - Error message or null if valid
 */
export const validateNumberRange = (value, min, max, fieldName) => {
  if (value === null || value === undefined || value === '') {
    return null; // Let required validation handle empty values
  }
  
  const numValue = Number(value);
  
  if (isNaN(numValue)) {
    return `${fieldName} must be a valid number`;
  }
  
  if (numValue < min) {
    return `${fieldName} must be at least ${min}`;
  }
  
  if (numValue > max) {
    return `${fieldName} must be no more than ${max}`;
  }
  
  return null;
};

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @returns {string|null} - Error message or null if valid
 */
export const validatePhone = (phone) => {
  if (!phone || phone.trim() === '') {
    return null; // Phone is optional in most cases
  }
  
  if (!PHONE_REGEX.test(phone)) {
    return 'Please enter a valid phone number';
  }
  
  return null;
};

/**
 * Validate time format (HH:MM)
 * @param {string} time - Time string to validate
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} - Error message or null if valid
 */
export const validateTime = (time, fieldName = 'Time') => {
  if (!time || time.trim() === '') {
    return `${fieldName} is required`;
  }
  
  if (!TIME_REGEX.test(time)) {
    return `${fieldName} must be in HH:MM format (24-hour)`;
  }
  
  return null;
};

/**
 * Validate date
 * @param {string|Date} date - Date to validate
 * @param {string} fieldName - Field name for error message
 * @param {Date} minDate - Minimum allowed date (optional)
 * @returns {string|null} - Error message or null if valid
 */
export const validateDate = (date, fieldName = 'Date', minDate = null) => {
  if (!date) {
    return `${fieldName} is required`;
  }
  
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return `${fieldName} must be a valid date`;
  }
  
  if (minDate && dateObj < minDate) {
    return `${fieldName} must be after ${minDate.toDateString()}`;
  }
  
  return null;
};

/**
 * Validate enum values
 * @param {any} value - Value to validate
 * @param {array} allowedValues - Array of allowed values
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} - Error message or null if valid
 */
export const validateEnum = (value, allowedValues, fieldName) => {
  if (!value) {
    return `${fieldName} is required`;
  }
  
  if (!allowedValues.includes(value)) {
    return `${fieldName} must be one of: ${allowedValues.join(', ')}`;
  }
  
  return null;
};

/**
 * Validate array field
 * @param {array} value - Array to validate
 * @param {string} fieldName - Field name for error message
 * @param {number} minLength - Minimum array length (optional)
 * @param {number} maxLength - Maximum array length (optional)
 * @returns {string|null} - Error message or null if valid
 */
export const validateArray = (value, fieldName, minLength = 0, maxLength = null) => {
  if (!Array.isArray(value)) {
    return `${fieldName} must be a list`;
  }
  
  if (value.length < minLength) {
    return `${fieldName} must have at least ${minLength} item${minLength !== 1 ? 's' : ''}`;
  }
  
  if (maxLength && value.length > maxLength) {
    return `${fieldName} must have no more than ${maxLength} item${maxLength !== 1 ? 's' : ''}`;
  }
  
  return null;
};

/**
 * Validate form data using validation rules
 * @param {object} data - Form data to validate
 * @param {object} rules - Validation rules object
 * @returns {object} - Object with field errors
 */
export const validateForm = (data, rules) => {
  const errors = {};
  
  Object.keys(rules).forEach(field => {
    const fieldRules = rules[field];
    const value = data[field];
    
    // Run each validation rule for the field
    for (const rule of fieldRules) {
      const error = rule(value, data);
      if (error) {
        errors[field] = error;
        break; // Stop at first error for this field
      }
    }
  });
  
  return errors;
};

/**
 * Check if form has any validation errors
 * @param {object} errors - Errors object from validateForm
 * @returns {boolean} - True if there are errors
 */
export const hasFormErrors = (errors) => {
  return Object.keys(errors).some(key => errors[key]);
};

/**
 * Get first error message from errors object
 * @param {object} errors - Errors object
 * @returns {string|null} - First error message or null
 */
export const getFirstError = (errors) => {
  const errorKeys = Object.keys(errors);
  if (errorKeys.length === 0) return null;
  
  return errors[errorKeys[0]];
};

// Common validation rule sets
export const userRegistrationRules = {
  username: [validateUsername],
  email: [validateEmail],
  password: [validatePassword],
  firstName: [(value) => validateRequired(value, 'First name'), (value) => validateStringLength(value, 1, 50, 'First name')],
  lastName: [(value) => validateRequired(value, 'Last name'), (value) => validateStringLength(value, 1, 50, 'Last name')],
  role: [(value) => validateEnum(value, ['member', 'trainer', 'admin'], 'Role')],
  phone: [validatePhone]
};

export const userLoginRules = {
  email: [validateEmail],
  password: [(value) => validateRequired(value, 'Password')]
};

export const planRules = {
  name: [(value) => validateRequired(value, 'Plan name'), (value) => validateStringLength(value, 1, 100, 'Plan name')],
  description: [(value) => validateRequired(value, 'Description'), (value) => validateStringLength(value, 1, 500, 'Description')],
  price: [(value) => validateRequired(value, 'Price'), (value) => validateNumberRange(value, 0, 10000, 'Price')],
  duration: [(value) => validateRequired(value, 'Duration'), (value) => validateNumberRange(value, 1, 365, 'Duration')]
};

export const facilityRules = {
  name: [(value) => validateRequired(value, 'Facility name'), (value) => validateStringLength(value, 1, 100, 'Facility name')],
  description: [(value) => validateRequired(value, 'Description'), (value) => validateStringLength(value, 1, 500, 'Description')],
  type: [(value) => validateEnum(value, ['gym', 'pool', 'court', 'studio', 'track'], 'Facility type')],
  capacity: [(value) => validateRequired(value, 'Capacity'), (value) => validateNumberRange(value, 1, 1000, 'Capacity')]
};